import React from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Chip,
  Alert,
  LinearProgress,
  CircularProgress,
  Divider,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  AccessTime as AccessTimeIcon,
  AttachMoney as MoneyIcon,
  Receipt as ReceiptIcon,
  PieChart as PieChartIcon,
  ShowChart as ShowChartIcon,
  Percent as PercentIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { financialCyclesService } from '../services/apiServices';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
} from 'recharts';

// ── formatters ────────────────────────────────────────────────────────────────

const fmt = (n: number) =>
  `GH₵ ${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtK = (v: number) => {
  const abs = Math.abs(v);
  if (abs >= 1_000_000) return `₵${(v / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `₵${(v / 1_000).toFixed(1)}k`;
  return `₵${v.toFixed(0)}`;
};

const fmtDate = (d: string) =>
  new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });

// ── colours ───────────────────────────────────────────────────────────────────

const SLICE_COLORS = ['#ff9800', '#42a5f5', '#66bb6a', '#ab47bc', '#26c6da', '#ef5350', '#26a69a'];
const AXIS_TICK = { fontSize: 11, fill: 'rgba(255,255,255,0.55)' } as const;
const GRID = 'rgba(255,255,255,0.07)';

// ── StatCard ──────────────────────────────────────────────────────────────────

function StatCard({
  label,
  value,
  sub,
  color,
  icon,
}: {
  label: string;
  value: React.ReactNode;
  sub?: string;
  color: string;
  icon: React.ReactNode;
}) {
  return (
    <Paper
      sx={{
        p: 2,
        flex: '1 1 150px',
        minWidth: 140,
        borderLeft: `4px solid ${color}`,
        position: 'relative',
        overflow: 'hidden',
        boxShadow: `inset 0 1px 0 ${color}22`,
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          right: -10,
          top: -10,
          fontSize: 70,
          opacity: 0.05,
          color,
          lineHeight: 1,
          pointerEvents: 'none',
          display: 'flex',
        }}
      >
        {icon}
      </Box>
      <Typography
        variant="caption"
        sx={{
          color: 'text.secondary',
          fontWeight: 600,
          letterSpacing: 0.8,
          textTransform: 'uppercase',
          fontSize: '0.62rem',
          display: 'block',
        }}
      >
        {label}
      </Typography>
      <Typography variant="h5" fontWeight={800} sx={{ color, mt: 0.5, lineHeight: 1.2 }}>
        {value}
      </Typography>
      {sub && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.25, display: 'block' }}>
          {sub}
        </Typography>
      )}
    </Paper>
  );
}

// ── Chart tooltips ────────────────────────────────────────────────────────────

const TrendsTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <Paper sx={{ p: 1.5, border: '1px solid rgba(255,255,255,0.1)', minWidth: 170 }}>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.75 }}>
        {label}
      </Typography>
      {payload.map((entry: any) => (
        <Box
          key={entry.name}
          sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mb: 0.25 }}
        >
          <Typography variant="caption" sx={{ color: entry.color }}>
            {entry.name}
          </Typography>
          <Typography variant="caption" fontWeight={700} sx={{ color: entry.color }}>
            {fmt(entry.value)}
          </Typography>
        </Box>
      ))}
    </Paper>
  );
};

const PieTooltip = ({ active, payload }: any) => {
  if (!active || !payload?.length) return null;
  const entry = payload[0];
  return (
    <Paper sx={{ p: 1.5, border: '1px solid rgba(255,255,255,0.1)' }}>
      <Typography variant="caption" color="text.secondary" display="block">
        {entry.name}
      </Typography>
      <Typography variant="body2" fontWeight={700} sx={{ color: entry.payload.fill ?? '#ff9800' }}>
        {fmt(entry.value)}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {entry.payload.pct?.toFixed(1)}% of expenses
      </Typography>
    </Paper>
  );
};

// ── MetricBar ─────────────────────────────────────────────────────────────────

function MetricBar({
  label,
  value,
  displayValue,
  max = 100,
  color,
}: {
  label: string;
  value: number;
  displayValue: string;
  max?: number;
  color: string;
}) {
  const pct = Math.min(Math.abs(value / max) * 100, 100);
  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
        <Typography variant="body2" fontWeight={700} color={color}>
          {displayValue}
        </Typography>
      </Box>
      <Box sx={{ height: 8, bgcolor: 'rgba(255,255,255,0.07)', borderRadius: 4 }}>
        <Box
          sx={{
            height: '100%',
            width: `${pct}%`,
            bgcolor: color,
            borderRadius: 4,
            transition: 'width 0.6s ease',
          }}
        />
      </Box>
    </Box>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

const FinancialCycleDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const {
    data: cycleData,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useQuery({
    queryKey: ['financialCycleDetail', id],
    queryFn: () => financialCyclesService.getById(id as string),
    enabled: !!id,
  });

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/financial/cycles')}
          sx={{ mb: 2, color: 'text.secondary' }}
        >
          Back to cycles
        </Button>
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => refetch()}>
              Retry
            </Button>
          }
        >
          {error instanceof Error ? error.message : 'Failed to load cycle details.'}
        </Alert>
      </Box>
    );
  }

  if (isLoading || !cycleData) {
    return (
      <Box
        sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}
      >
        <CircularProgress color="primary" />
      </Box>
    );
  }

  // ── parse API response ──────────────────────────────────────────────────────
  const parseAmount = (s: string): number => parseFloat(s.replace(/[GH₵\s,]/g, '')) || 0;

  const cycleStats = cycleData.cycle_statistics;
  const cycleInfo = cycleStats.cycle_info;
  const fin = cycleStats.financial_summary;
  const costBreakdownRaw = cycleStats.cost_breakdown || [];
  const dailyTrendsRaw = cycleStats.daily_trends || [];

  const cycle = {
    name: cycleInfo.cycle_number as string,
    start_date: cycleInfo.start_date as string,
    end_date: cycleInfo.end_date as string | undefined,
    status: cycleInfo.status as string,
  };

  const revenue = parseAmount(fin.total_sales);
  const expenses = parseAmount(fin.total_costs);
  const profit = parseAmount(fin.profit);
  const profitMargin: number = fin.profit_margin || 0;
  const roi: number = fin.roi || 0;
  const isProfit = profit >= 0;

  const durationDays = Math.max(
    1,
    Math.floor(
      (new Date(cycle.end_date ?? new Date()).getTime() - new Date(cycle.start_date).getTime()) /
        (1000 * 60 * 60 * 24)
    )
  );

  // cost breakdown — attach fill colour and % to each slice
  const costBreakdown = costBreakdownRaw.map((item: any, i: number) => ({
    name: item.category as string,
    value: parseAmount(item.total_amount),
    fill: SLICE_COLORS[i % SLICE_COLORS.length],
    pct: expenses > 0 ? (parseAmount(item.total_amount) / expenses) * 100 : 0,
  }));

  // daily trends — already divided /100 in original code
  const dailyTrends = dailyTrendsRaw.map((item: any) => ({
    date: item.day_name as string,
    Sales: parseFloat(item.sales) / 100,
    Costs: parseFloat(item.costs) / 100,
    Profit: parseFloat(item.profit) / 100,
  }));

  const marginColor = profitMargin >= 0 ? '#4caf50' : '#f44336';
  const roiColor = roi >= 0 ? '#4caf50' : '#f44336';

  return (
    <Box sx={{ p: 3 }}>
      {isFetching && (
        <LinearProgress
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 9999,
            height: 3,
            '& .MuiLinearProgress-bar': { background: 'linear-gradient(90deg, #ff9800, #ffa726)' },
          }}
        />
      )}

      {/* ── Header ── */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3, flexWrap: 'wrap' }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/financial/cycles')}
          variant="outlined"
          size="small"
          sx={{ borderColor: 'rgba(255,255,255,0.2)', color: 'text.secondary', flexShrink: 0 }}
        >
          Cycles
        </Button>

        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: 2,
            background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 12px rgba(255,152,0,0.4)',
            flexShrink: 0,
          }}
        >
          <ShowChartIcon sx={{ color: '#000', fontSize: 22 }} />
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h5" fontWeight={700} noWrap>
            {cycle.name}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {fmtDate(cycle.start_date)}
            {cycle.end_date ? ` – ${fmtDate(cycle.end_date)}` : ' – Active'} · {durationDays} days
          </Typography>
        </Box>

        <Chip
          label={cycle.status}
          size="small"
          color={cycle.status === 'active' ? 'success' : 'default'}
          sx={{ textTransform: 'capitalize', fontWeight: 700 }}
        />
      </Box>

      {/* ── Summary strip ── */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <StatCard
          label="Revenue"
          value={fmtK(revenue)}
          sub={fmt(revenue)}
          color="#66bb6a"
          icon={<MoneyIcon sx={{ fontSize: 'inherit' }} />}
        />
        <StatCard
          label="Expenses"
          value={fmtK(expenses)}
          sub={fmt(expenses)}
          color="#ef5350"
          icon={<ReceiptIcon sx={{ fontSize: 'inherit' }} />}
        />
        <StatCard
          label={isProfit ? 'Net Profit' : 'Net Loss'}
          value={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {isProfit ? (
                <TrendingUpIcon sx={{ fontSize: 22 }} />
              ) : (
                <TrendingDownIcon sx={{ fontSize: 22 }} />
              )}
              {fmtK(Math.abs(profit))}
            </Box>
          }
          sub={fmt(Math.abs(profit))}
          color={isProfit ? '#4caf50' : '#f44336'}
          icon={
            isProfit ? (
              <TrendingUpIcon sx={{ fontSize: 'inherit' }} />
            ) : (
              <TrendingDownIcon sx={{ fontSize: 'inherit' }} />
            )
          }
        />
        <StatCard
          label="Profit Margin"
          value={`${profitMargin.toFixed(1)}%`}
          sub="Revenue → profit rate"
          color={marginColor}
          icon={<PercentIcon sx={{ fontSize: 'inherit' }} />}
        />
        <StatCard
          label="ROI"
          value={`${roi.toFixed(1)}%`}
          sub="Return on investment"
          color={roiColor}
          icon={<AccessTimeIcon sx={{ fontSize: 'inherit' }} />}
        />
      </Box>

      {/* ── Charts row ── */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: '1.7fr 1fr' },
          gap: 2,
          mb: 2,
        }}
      >
        {/* Daily trends area chart */}
        <Paper sx={{ p: 2.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <ShowChartIcon sx={{ fontSize: 16, color: '#ff9800' }} />
            <Typography variant="subtitle2" fontWeight={700}>
              Financial Trends
            </Typography>
          </Box>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
            Daily sales, costs, and net profit over the cycle
          </Typography>

          {dailyTrends.length === 0 ? (
            <Box sx={{ py: 6, textAlign: 'center' }}>
              <Typography color="text.secondary" variant="body2">
                No trend data available
              </Typography>
            </Box>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <AreaChart data={dailyTrends} margin={{ left: 4, right: 4, top: 4, bottom: 4 }}>
                <defs>
                  <linearGradient id="salesGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#4caf50" stopOpacity={0.25} />
                    <stop offset="95%" stopColor="#4caf50" stopOpacity={0.02} />
                  </linearGradient>
                  <linearGradient id="costsGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#f44336" stopOpacity={0.25} />
                    <stop offset="95%" stopColor="#f44336" stopOpacity={0.02} />
                  </linearGradient>
                  <linearGradient id="profitGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#42a5f5" stopOpacity={0.2} />
                    <stop offset="95%" stopColor="#42a5f5" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={GRID} vertical={false} />
                <XAxis dataKey="date" tick={AXIS_TICK} axisLine={false} tickLine={false} />
                <YAxis
                  tickFormatter={fmtK}
                  tick={AXIS_TICK}
                  axisLine={false}
                  tickLine={false}
                  width={56}
                />
                <RechartsTooltip
                  content={(props: any) => <TrendsTooltip {...props} />}
                  cursor={{ stroke: 'rgba(255,255,255,0.1)', strokeWidth: 1 }}
                />
                <Legend
                  formatter={(value: string) => (
                    <Typography component="span" variant="caption" color="text.secondary">
                      {value}
                    </Typography>
                  )}
                  iconSize={8}
                  iconType="circle"
                />
                <Area
                  type="monotone"
                  dataKey="Sales"
                  stroke="#4caf50"
                  strokeWidth={2}
                  fill="url(#salesGrad)"
                  dot={false}
                  activeDot={{ r: 4, fill: '#4caf50' }}
                />
                <Area
                  type="monotone"
                  dataKey="Costs"
                  stroke="#f44336"
                  strokeWidth={2}
                  fill="url(#costsGrad)"
                  dot={false}
                  activeDot={{ r: 4, fill: '#f44336' }}
                />
                <Area
                  type="monotone"
                  dataKey="Profit"
                  stroke="#42a5f5"
                  strokeWidth={2}
                  strokeDasharray="5 3"
                  fill="url(#profitGrad)"
                  dot={false}
                  activeDot={{ r: 4, fill: '#42a5f5' }}
                />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </Paper>

        {/* Cost breakdown donut */}
        <Paper sx={{ p: 2.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <PieChartIcon sx={{ fontSize: 16, color: '#ff9800' }} />
            <Typography variant="subtitle2" fontWeight={700}>
              Cost Breakdown
            </Typography>
          </Box>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
            Where expenses are allocated
          </Typography>

          {costBreakdown.length === 0 ? (
            <Box sx={{ py: 6, textAlign: 'center' }}>
              <Typography color="text.secondary" variant="body2">
                No cost data available
              </Typography>
            </Box>
          ) : (
            <>
              {/* Donut with centre label */}
              <Box sx={{ position: 'relative' }}>
                <ResponsiveContainer width="100%" height={190}>
                  <PieChart>
                    <Pie
                      data={costBreakdown}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      innerRadius={52}
                      outerRadius={80}
                      paddingAngle={2}
                    />
                    <RechartsTooltip content={(props: any) => <PieTooltip {...props} />} />
                  </PieChart>
                </ResponsiveContainer>
                <Box
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    textAlign: 'center',
                    pointerEvents: 'none',
                  }}
                >
                  <Typography variant="caption" color="text.secondary" display="block">
                    Total
                  </Typography>
                  <Typography variant="body2" fontWeight={800} color="#ef5350">
                    {fmtK(expenses)}
                  </Typography>
                </Box>
              </Box>

              {/* Legend rows */}
              <Divider sx={{ borderColor: 'rgba(255,255,255,0.07)', mb: 1.5 }} />
              {costBreakdown.map(
                (item: { name: string; value: number; fill: string; pct: number }) => (
                  <Box
                    key={item.name}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      mb: 1,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: '50%',
                          bgcolor: item.fill,
                          flexShrink: 0,
                        }}
                      />
                      <Typography variant="body2" color="text.secondary">
                        {item.name}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Typography variant="caption" color="text.secondary">
                        {item.pct.toFixed(1)}%
                      </Typography>
                      <Typography variant="body2" fontWeight={700}>
                        {fmt(item.value)}
                      </Typography>
                    </Box>
                  </Box>
                )
              )}
            </>
          )}
        </Paper>
      </Box>

      {/* ── Performance metrics + Cycle info ── */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: 2,
        }}
      >
        {/* Key metrics */}
        <Paper sx={{ p: 2.5 }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2 }}>
            Key Metrics
          </Typography>

          <MetricBar
            label="Profit Margin"
            value={profitMargin}
            displayValue={`${profitMargin.toFixed(1)}%`}
            color={marginColor}
          />
          <MetricBar
            label="Return on Investment (ROI)"
            value={roi}
            displayValue={`${roi.toFixed(1)}%`}
            color={roiColor}
          />

          <Divider sx={{ borderColor: 'rgba(255,255,255,0.07)', my: 2 }} />

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 1.5,
            }}
          >
            {[
              {
                label: 'Avg Daily Sales',
                value: fmt(revenue / durationDays),
                color: '#66bb6a',
              },
              {
                label: 'Avg Daily Costs',
                value: fmt(expenses / durationDays),
                color: '#ef5350',
              },
              {
                label: 'Avg Daily Profit',
                value: fmt(profit / durationDays),
                color: isProfit ? '#4caf50' : '#f44336',
              },
              {
                label: 'Cycle Length',
                value: `${durationDays} days`,
                color: '#ff9800',
              },
            ].map((m) => (
              <Paper
                key={m.label}
                sx={{
                  p: 1.5,
                  bgcolor: 'rgba(255,255,255,0.02)',
                  border: '1px solid rgba(255,255,255,0.07)',
                }}
              >
                <Typography variant="caption" color="text.secondary" display="block">
                  {m.label}
                </Typography>
                <Typography variant="body2" fontWeight={700} color={m.color}>
                  {m.value}
                </Typography>
              </Paper>
            ))}
          </Box>
        </Paper>

        {/* Cycle info + financial health */}
        <Paper sx={{ p: 2.5 }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2 }}>
            Cycle Information
          </Typography>

          {[
            { label: 'Cycle', value: cycle.name },
            { label: 'Start Date', value: fmtDate(cycle.start_date) },
            { label: 'End Date', value: cycle.end_date ? fmtDate(cycle.end_date) : '— Ongoing' },
            { label: 'Duration', value: `${durationDays} days` },
          ].map((row) => (
            <Box
              key={row.label}
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                py: 0.875,
                borderBottom: '1px solid rgba(255,255,255,0.05)',
              }}
            >
              <Typography variant="body2" color="text.secondary">
                {row.label}
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {row.value}
              </Typography>
            </Box>
          ))}

          <Divider sx={{ borderColor: 'rgba(255,255,255,0.07)', my: 2 }} />

          {/* Financial health banner */}
          <Box
            sx={{
              p: 2,
              borderRadius: 2,
              border: `1px solid ${isProfit ? 'rgba(76,175,80,0.35)' : 'rgba(244,67,54,0.35)'}`,
              background: isProfit
                ? 'linear-gradient(135deg, rgba(76,175,80,0.1) 0%, transparent 100%)'
                : 'linear-gradient(135deg, rgba(244,67,54,0.1) 0%, transparent 100%)',
              display: 'flex',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: isProfit ? 'rgba(76,175,80,0.2)' : 'rgba(244,67,54,0.2)',
                border: `2px solid ${isProfit ? '#4caf50' : '#f44336'}`,
                flexShrink: 0,
              }}
            >
              {isProfit ? (
                <TrendingUpIcon sx={{ color: '#4caf50', fontSize: 22 }} />
              ) : (
                <TrendingDownIcon sx={{ color: '#f44336', fontSize: 22 }} />
              )}
            </Box>
            <Box>
              <Typography
                variant="subtitle2"
                fontWeight={700}
                color={isProfit ? '#4caf50' : '#f44336'}
              >
                {isProfit ? 'Profitable Cycle' : 'Loss-Making Cycle'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {isProfit
                  ? `${profitMargin.toFixed(1)}% margin · ${roi.toFixed(1)}% ROI`
                  : `Expenses exceed revenue by ${fmt(Math.abs(profit))}`}
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default FinancialCycleDetailPage;
